import React, { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import './OrderDetailModal.css';

// OrderDetailModal — admin view of one SMM order with editable notes.
//
// Props
//   orderId:  Mongo _id of the order (string) OR null to keep closed
//   onClose:  () => void
//
// Hits GET /api/admin/orders/:id on open and PUT /api/admin/orders/:id/note
// on save. Notes are free-form (4000-char cap server-side) — use them for
// retry context, refund history, "promised X by Y," etc.

const STATUS_COLORS = {
  Pending: '#fbbf24',
  'In progress': '#60a5fa',
  Processing: '#60a5fa',
  Partial: '#f59e0b',
  Completed: '#34d399',
  Canceled: '#f87171',
  Refunded: '#a78bfa',
};

const fmtUsd = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
const fmtInt = (v) => (v == null ? '—' : new Intl.NumberFormat().format(v));
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
};
const fmtRelative = (d) => {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const OrderDetailModal = ({ orderId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Refund state
  const [refundPreview, setRefundPreview] = useState(null);
  const [walletInfo, setWalletInfo] = useState(null);
  const [refundOverrideUsd, setRefundOverrideUsd] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [processingRefund, setProcessingRefund] = useState(false);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/admin/orders/${orderId}`);
      setData(res.data);
      setNoteDraft(res.data?.order?.admin_note || '');
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const loadRefundContext = useCallback(async () => {
    if (!orderId) return;
    try {
      const [p, w] = await Promise.all([
        api.get(`/admin/orders/${orderId}/refund-preview`),
        api.get('/admin/refunds/wallet'),
      ]);
      setRefundPreview(p.data);
      setWalletInfo(w.data);
    } catch (_) { /* silent — section just won't render */ }
  }, [orderId]);

  useEffect(() => {
    if (orderId) { load(); loadRefundContext(); }
    else {
      setData(null); setNoteDraft(''); setError('');
      setRefundPreview(null); setWalletInfo(null);
      setRefundOverrideUsd(''); setRefundReason('');
    }
  }, [orderId, load, loadRefundContext]);

  // Esc to close
  useEffect(() => {
    if (!orderId) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [orderId, onClose]);

  const handleProcessRefund = async () => {
    if (!data?.order?.db_id) return;
    const computedUsd = refundPreview?.computed?.refundUsd ?? 0;
    const overrideNum = refundOverrideUsd === '' ? null : Number(refundOverrideUsd);
    const finalUsd = overrideNum != null ? overrideNum : computedUsd;

    if (!Number.isFinite(finalUsd) || finalUsd <= 0) {
      toast.error('Refund amount must be greater than 0');
      return;
    }

    const ok = window.confirm(
      `Refund $${finalUsd.toFixed(2)} USDT to ${data.order.wallet.slice(0, 6)}…${data.order.wallet.slice(-4)}?\n\n` +
      'This sends real USDT from the refund wallet. It cannot be undone.'
    );
    if (!ok) return;

    setProcessingRefund(true);
    try {
      const body = { reason: refundReason || 'admin manual refund' };
      if (overrideNum != null) body.override_usd = overrideNum;
      const res = await api.post(`/admin/orders/${data.order.db_id}/refund`, body);
      toast.success(`Refund sent: ${res.data.refund_usd} USDT — tx ${res.data.tx_hash.slice(0, 10)}…`);
      // Reload everything so the UI reflects the new state.
      await load();
      await loadRefundContext();
      setRefundOverrideUsd(''); setRefundReason('');
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Refund failed';
      toast.error(msg);
    } finally {
      setProcessingRefund(false);
    }
  };

  const handleSaveNote = async () => {
    if (!data?.order?.db_id) return;
    setSavingNote(true);
    try {
      const res = await api.put(`/admin/orders/${data.order.db_id}/note`, { note: noteDraft });
      setData((d) => d ? {
        ...d,
        order: {
          ...d.order,
          admin_note: res.data.admin_note,
          admin_note_updated_at: res.data.admin_note_updated_at,
          admin_note_updated_by: res.data.admin_note_updated_by,
        },
      } : d);
      toast.success('Note saved');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  if (!orderId) return null;

  const order = data?.order;
  const noteDirty = order && noteDraft !== (order.admin_note || '');

  return (
    <div className="order-detail-modal-overlay" onClick={onClose}>
      <div className="order-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button className="order-detail-close" type="button" onClick={onClose} aria-label="Close">×</button>

        <header className="order-detail-head">
          <span className="order-detail-eyebrow">Order Detail</span>
          {order && (
            <>
              <h2 className="order-detail-title">
                {order.internal_order_id || `#${order.db_id.slice(-6)}`}
              </h2>
              <div className="order-detail-subtitle">
                <span
                  className="order-detail-status"
                  style={{
                    color: STATUS_COLORS[order.status] || '#aaa',
                    borderColor: (STATUS_COLORS[order.status] || '#aaa') + '55',
                    background: (STATUS_COLORS[order.status] || '#aaa') + '15',
                  }}
                >
                  {order.status}
                </span>
                <span className="order-detail-amount">{fmtUsd(order.charged_usd)}</span>
                <span className="order-detail-meta-dot">·</span>
                <span className="order-detail-when">{fmtRelative(order.created_at)}</span>
              </div>
            </>
          )}
        </header>

        {loading && <div className="order-detail-loading">Loading order…</div>}
        {error && <div className="order-detail-error">⚠ {error}</div>}

        {order && (
          <>
            <section className="order-detail-grid">
              <Field label="Wallet" value={<code className="order-detail-mono">{order.wallet}</code>} wide />
              <Field label="Service" value={order.service_name || '—'} wide />
              <Field label="Service ID" value={order.service_id || '—'} />
              <Field label="Quantity" value={fmtInt(order.quantity)} />
              <Field label="Internal # (shown to user)" value={order.internal_order_id || '—'} />
              <Field label="Provider" value={order.provider || 'smmflare'} />
              <Field label="Provider # (upstream)" value={order.provider_order_id || '—'} />
              <Field label="DB ID" value={<code className="order-detail-mono">{order.db_id}</code>} />
              <Field label="Start count" value={fmtInt(order.start_count)} />
              <Field label="Remains" value={fmtInt(order.remains)} />
              <Field label="Provider rate /1K" value={order.provider_rate_per_1k != null ? `$${Number(order.provider_rate_per_1k).toFixed(4)}` : '—'} />
              <Field label="Sell rate /1K" value={order.sell_rate_per_1k != null ? `$${Number(order.sell_rate_per_1k).toFixed(4)}` : '—'} />
              <Field
                label="Charged"
                value={
                  <>
                    {fmtUsd(order.charged_usd)}
                    {order.discount_applied != null && order.discount_applied > 0 && (
                      <span className="order-detail-discount">
                        {' '}(−{order.discount_applied}% from {fmtUsd(order.original_charged_usd)})
                      </span>
                    )}
                  </>
                }
              />
              <Field label="Payment" value={order.payment_currency || '—'} />
              <Field label="Escrow order ID" value={order.escrow_order_id || '—'} wide mono />
              <Field label="Link" value={order.link
                ? <a className="order-detail-link" href={order.link} target="_blank" rel="noopener noreferrer">{order.link}</a>
                : '—'
              } wide />
              <Field label="Created" value={fmtDate(order.created_at)} />
              <Field label="Updated" value={fmtDate(order.updated_at)} />
              {order.reconciled_at && (
                <Field label="Reconciled" value={`${fmtDate(order.reconciled_at)} · ${order.reconciled_by || '?'}`} wide />
              )}
            </section>

            {data?.open_refund_ticket_id && (
              <div className="order-detail-banner order-detail-banner--warn">
                ⚠ This order has an OPEN refund ticket. Resolve it under <b>PHONE Tickets</b>.
              </div>
            )}

            {/* Refund — Phase 1: direct USDT to user wallet ─────────────── */}
            {refundPreview && (() => {
              const refState = order.refund_state || 'none';
              const computed = refundPreview.computed || {};
              const ineligibleStatus = !['Partial', 'Canceled'].includes(order.status);
              const alreadyCompleted = refState === 'completed';
              const inProgress = refState === 'in_progress';
              const walletConfigured = walletInfo?.configured;
              const overrideNum = refundOverrideUsd === '' ? null : Number(refundOverrideUsd);
              const finalUsd = overrideNum != null ? overrideNum : (computed.refundUsd || 0);
              const overCap = walletInfo && finalUsd > Number(walletInfo.per_order_cap_usd || 0);
              const underfunded = walletInfo && walletInfo.balance_usdt != null
                && Number(walletInfo.balance_usdt) < finalUsd;
              const canRefund = !ineligibleStatus && !alreadyCompleted && !inProgress &&
                walletConfigured && !overCap && !underfunded && finalUsd > 0 && !processingRefund;

              return (
                <section className="order-detail-refund">
                  <div className="order-detail-refund-head">
                    <span className="order-detail-refund-label">Refund</span>
                    {alreadyCompleted && (
                      <span className="order-detail-refund-pill order-detail-refund-pill--ok">
                        ✓ Refunded {fmtUsd(order.refunded_usd)}
                      </span>
                    )}
                    {inProgress && (
                      <span className="order-detail-refund-pill order-detail-refund-pill--warn">
                        ⏳ In progress
                      </span>
                    )}
                    {refState === 'failed' && (
                      <span className="order-detail-refund-pill order-detail-refund-pill--err">
                        ✕ Last refund failed — retry available
                      </span>
                    )}
                  </div>

                  {/* The math */}
                  <div className="order-detail-refund-math">
                    <Field label="Quantity" value={fmtInt(order.quantity)} />
                    <Field label="Delivered" value={fmtInt(computed.deliveredQty)} />
                    <Field label="Undelivered" value={fmtInt(computed.refundQty)} />
                    <Field
                      label="Computed refund"
                      value={
                        <>
                          {fmtUsd(computed.refundUsd)}
                          <span className="order-detail-refund-pct"> · {computed.refundFractionPct}% of paid</span>
                        </>
                      }
                    />
                  </div>

                  {/* Wallet status strip */}
                  {walletInfo && (
                    <div className="order-detail-refund-wallet">
                      {walletConfigured ? (
                        <>
                          <span>From <code className="order-detail-mono">{walletInfo.address?.slice(0, 6)}…{walletInfo.address?.slice(-4)}</code></span>
                          <span>Balance: <b>{walletInfo.balance_usdt} USDT</b></span>
                          <span>Auto-cap: ${walletInfo.auto_cap_usd}</span>
                          <span>Per-order cap: ${walletInfo.per_order_cap_usd}</span>
                        </>
                      ) : (
                        <span className="order-detail-refund-warn">
                          ⚠ <code>REFUND_WALLET_PRIVATE_KEY</code> is not set in the backend env — refunds are disabled.
                        </span>
                      )}
                    </div>
                  )}

                  {/* Action — only show if there's something actionable */}
                  {!alreadyCompleted && !inProgress && (
                    <>
                      {ineligibleStatus && (
                        <div className="order-detail-refund-msg">
                          Only orders with status <b>Partial</b> or <b>Canceled</b> can be refunded.
                          Current status: <b>{order.status}</b>.
                        </div>
                      )}
                      {underfunded && walletConfigured && (
                        <div className="order-detail-refund-msg order-detail-refund-msg--err">
                          ⚠ Refund wallet has {walletInfo.balance_usdt} USDT — needs ${finalUsd.toFixed(2)}. Top it up first.
                        </div>
                      )}
                      {overCap && (
                        <div className="order-detail-refund-msg order-detail-refund-msg--err">
                          ⚠ Refund ${finalUsd.toFixed(2)} exceeds per-order cap (${walletInfo.per_order_cap_usd}).
                        </div>
                      )}

                      <div className="order-detail-refund-controls">
                        <label className="order-detail-refund-field">
                          <span>Override amount (USD)</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder={String(computed.refundUsd || 0)}
                            value={refundOverrideUsd}
                            onChange={(e) => setRefundOverrideUsd(e.target.value)}
                            disabled={processingRefund || !walletConfigured}
                          />
                        </label>
                        <label className="order-detail-refund-field order-detail-refund-field--wide">
                          <span>Reason (audit)</span>
                          <input
                            type="text"
                            placeholder="e.g. provider partial delivery, customer requested cancel"
                            value={refundReason}
                            onChange={(e) => setRefundReason(e.target.value)}
                            disabled={processingRefund || !walletConfigured}
                            maxLength={500}
                          />
                        </label>
                        <button
                          type="button"
                          className="order-detail-refund-go"
                          onClick={handleProcessRefund}
                          disabled={!canRefund}
                          title={!walletConfigured ? 'Refund wallet not configured' : ''}
                        >
                          {processingRefund
                            ? 'Sending…'
                            : `Refund $${finalUsd.toFixed(2)}`}
                        </button>
                      </div>
                    </>
                  )}

                  {/* Past refund history */}
                  {refundPreview.history?.length > 0 && (
                    <div className="order-detail-refund-history">
                      <div className="order-detail-refund-history-head">History</div>
                      <ul className="order-detail-refund-history-list">
                        {refundPreview.history.map((h) => (
                          <li key={h.id} className={`order-detail-refund-history-row order-detail-refund-history-row--${h.tx_status}`}>
                            <span className="order-detail-refund-history-amt">{fmtUsd(h.refund_usd)}</span>
                            <span className="order-detail-refund-history-tx">{h.tx_status}</span>
                            {h.tx_hash && (
                              <a
                                className="order-detail-refund-history-link"
                                href={`https://bscscan.com/tx/${h.tx_hash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {h.tx_hash.slice(0, 10)}…
                              </a>
                            )}
                            <span className="order-detail-refund-history-src">{h.source} · {h.initiated_by === 'system' ? 'system' : `${h.initiated_by.slice(0, 6)}…${h.initiated_by.slice(-4)}`}</span>
                            <span className="order-detail-refund-history-when">{fmtRelative(h.created_at)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </section>
              );
            })()}

            {/* Notes — the main reason this modal exists */}
            <section className="order-detail-note">
              <div className="order-detail-note-head">
                <span className="order-detail-note-label">Admin notes</span>
                {order.admin_note_updated_at && (
                  <span className="order-detail-note-meta">
                    last edited {fmtRelative(order.admin_note_updated_at)}
                    {order.admin_note_updated_by && ` by ${order.admin_note_updated_by.slice(0, 6)}…${order.admin_note_updated_by.slice(-4)}`}
                  </span>
                )}
              </div>
              <textarea
                className="order-detail-note-input"
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="e.g. Retry of #9803 after provider returned 'invalid link' — customer Telegram @foo confirmed link is correct."
                maxLength={4000}
                rows={5}
              />
              <div className="order-detail-note-actions">
                <span className="order-detail-note-count">{noteDraft.length} / 4000</span>
                <button
                  type="button"
                  className="order-detail-note-save"
                  onClick={handleSaveNote}
                  disabled={!noteDirty || savingNote}
                >
                  {savingNote ? 'Saving…' : noteDirty ? 'Save note' : 'Saved'}
                </button>
              </div>
            </section>

            {data?.same_wallet_recent?.length > 0 && (
              <section className="order-detail-related">
                <div className="order-detail-related-head">
                  <span className="order-detail-related-label">
                    Other recent orders from this wallet
                  </span>
                </div>
                <ul className="order-detail-related-list">
                  {data.same_wallet_recent.filter((o) => o.db_id !== order.db_id).map((o) => (
                    <li key={o.db_id} className="order-detail-related-row">
                      <span
                        className="order-detail-related-status"
                        style={{
                          color: STATUS_COLORS[o.status] || '#aaa',
                          background: (STATUS_COLORS[o.status] || '#aaa') + '15',
                          borderColor: (STATUS_COLORS[o.status] || '#aaa') + '55',
                        }}
                      >
                        {o.status}
                      </span>
                      <span className="order-detail-related-svc" title={o.service_name}>
                        {o.service_name || '—'}
                      </span>
                      <span className="order-detail-related-amt">{fmtUsd(o.charged_usd)}</span>
                      <span className="order-detail-related-when">{fmtRelative(o.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, wide, mono }) => (
  <div className={`order-detail-field ${wide ? 'order-detail-field--wide' : ''}`}>
    <span className="order-detail-field-label">{label}</span>
    <span className={`order-detail-field-value ${mono ? 'order-detail-mono' : ''}`}>{value}</span>
  </div>
);

export default OrderDetailModal;
